import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Check, Landmark, QrCode, CreditCard } from 'lucide-react';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * The right half of the sign-in screen.
 *
 * It shows the product rather than describing it: a scaled-down hosted checkout,
 * re-skinning itself between three imaginary merchants on a slow loop. That is
 * the whole pitch — the same checkout, carrying whoever's brand is paying for
 * it — and it makes the point in less time than the paragraph underneath it.
 *
 * Everything here is decorative and inert. No network call, no real amount, no
 * interactive control that could be tabbed into from the form on the left.
 */
const BRANDS = [
  {
    name: 'Bean There',
    initial: 'B',
    accent: '#7a4a2b',
    amount: '$18.50',
    line: 'Two bags of Ethiopian',
    methods: [CreditCard, Landmark],
  },
  {
    name: 'Nordwell Studio',
    initial: 'N',
    accent: '#1f6f5c',
    amount: '£240.00',
    line: 'Brand identity — deposit',
    methods: [CreditCard],
  },
  {
    name: 'Saanvi Textiles',
    initial: 'S',
    accent: '#8a2f4d',
    amount: '₹6,000.00',
    line: 'Order #4821',
    methods: [CreditCard, QrCode, Landmark],
  },
];

const ROTATE_MS = 4200;

function CheckoutPreview({ brand }) {
  return (
    <motion.div
      key={brand.name}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="w-full overflow-hidden rounded-2xl border bg-surface"
      style={{ borderColor: 'rgb(var(--line))', boxShadow: 'var(--shadow-lg, 0 18px 50px -20px rgb(0 0 0 / 0.22))' }}
    >
      {/* The merchant's own header. Nothing on this card names a processor. */}
      <div className="flex items-center gap-2.5 border-b px-5 py-4" style={{ borderColor: 'rgb(var(--line-soft))' }}>
        <span
          className="grid h-7 w-7 place-items-center rounded-lg text-[13px] font-semibold text-white"
          style={{ background: brand.accent }}
        >
          {brand.initial}
        </span>
        <span className="text-sm font-medium text-ink">{brand.name}</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-faint">
          <Lock className="h-2.5 w-2.5" aria-hidden="true" />
          Secure
        </span>
      </div>

      <div className="px-5 py-5">
        <p className="eyebrow">Amount due</p>
        <p className="tabular mt-1 text-[26px] font-semibold leading-none tracking-tight text-ink">
          {brand.amount}
        </p>
        <p className="mt-1.5 text-xs text-muted">{brand.line}</p>

        <div className="mt-5 space-y-2">
          {/* A card form, drawn as the shapes of one. */}
          <div className="h-9 rounded-lg border bg-surface-2" style={{ borderColor: 'rgb(var(--line))' }} />
          <div className="grid grid-cols-2 gap-2">
            <div className="h-9 rounded-lg border bg-surface-2" style={{ borderColor: 'rgb(var(--line))' }} />
            <div className="h-9 rounded-lg border bg-surface-2" style={{ borderColor: 'rgb(var(--line))' }} />
          </div>
        </div>

        <div
          className="mt-4 flex h-10 items-center justify-center gap-2 rounded-lg text-[13px] font-semibold text-white"
          style={{ background: brand.accent }}
        >
          <Lock className="h-3 w-3" aria-hidden="true" />
          Pay {brand.amount}
        </div>

        {/* The methods this merchant happens to accept — the same page adapts. */}
        <div className="mt-4 flex items-center gap-1.5 border-t pt-3.5" style={{ borderColor: 'rgb(var(--line-soft))' }}>
          {brand.methods.map((Icon, i) => (
            <span
              key={i}
              className="grid h-6 w-6 place-items-center rounded-md border bg-surface-2 text-faint"
              style={{ borderColor: 'rgb(var(--line))' }}
            >
              <Icon className="h-3 w-3" aria-hidden="true" />
            </span>
          ))}
          <span className="ml-auto text-[10px] text-faint">{brand.name}</span>
        </div>
      </div>
    </motion.div>
  );
}

export function AuthShowcase() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // Someone who asked for less motion gets one brand and no loop.
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) { setPaused(true); return undefined; }

    const timer = setInterval(() => setIndex((i) => (i + 1) % BRANDS.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  const brand = BRANDS[index];

  return (
    <div
      aria-hidden="true"
      className="relative hidden overflow-hidden border-l lg:block"
      style={{ borderColor: 'rgb(var(--line))' }}
    >
      {/* Two radial bleeds over a vertical ramp — the same wash the page heroes use. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(900px 420px at 85% 0%, rgb(var(--accent-500) / 0.08), transparent 62%),' +
            'radial-gradient(700px 320px at 5% 100%, rgb(var(--accent-400) / 0.06), transparent 60%),' +
            'linear-gradient(180deg, rgb(var(--surface)) 0%, rgb(var(--surface-2)) 100%)',
        }}
      />
      {/* A faint grid, so the card has something to sit on rather than a void. */}
      <div
        className="absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--line) / 0.5) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgb(var(--line) / 0.5) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(70% 60% at 50% 40%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(70% 60% at 50% 40%, #000 30%, transparent 100%)',
        }}
      />

      <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
        <div className="max-w-md">
          <h2 className="text-[26px] font-semibold leading-[1.15] tracking-tight text-ink xl:text-[30px]">
            Your customers see your brand.
            <br />
            Not ours, and not your processor&rsquo;s.
          </h2>
          <p className="mt-3.5 text-sm leading-relaxed text-muted">
            Connect the payment provider you already have. Integrate once, and keep that integration
            when you add another — or when you decide to take a bank transfer instead.
          </p>
        </div>

        <div className="my-8 flex justify-center">
          <div className="w-full max-w-[290px]">
            <AnimatePresence mode="wait">
              <CheckoutPreview brand={brand} />
            </AnimatePresence>

            <div className="mt-4 flex items-center justify-center gap-1.5">
              {BRANDS.map((b, i) => (
                <span
                  key={b.name}
                  className="h-1 rounded-full transition-all duration-300"
                  style={{
                    width: i === index ? 18 : 6,
                    background: i === index ? 'rgb(var(--ink-soft))' : 'rgb(var(--line))',
                  }}
                />
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] text-faint">
              {paused ? 'One checkout, every brand.' : 'One checkout. Three merchants.'}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-3 gap-6 border-t pt-6" style={{ borderColor: 'rgb(var(--line))' }}>
          {[
            ['One API', 'for every provider'],
            ['No card data', 'ever touches us'],
            ['Signed webhooks', 'with retries'],
          ].map(([term, detail]) => (
            <div key={term}>
              <dt className="flex items-center gap-1.5 text-sm font-medium text-ink">
                <Check className="h-3 w-3 text-accent-ink" aria-hidden="true" />
                {term}
              </dt>
              <dd className="mt-0.5 text-xs leading-snug text-muted">{detail}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export default AuthShowcase;
